import { useCallback, useEffect, useRef, useState } from "react";
import { trackBuilderEvent } from "../../../helpers/builderAnalytics";
import { encodeGif } from "../../../helpers/modelBuilder/gifEncoder";

export default function useResultsGifExport({ stageRef, simulationId, selectedRunId, totalTime, time, loadedTime, loading, isVisible, setTime, setPlaying, setError }) {
  const jobRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const cancel = useCallback(() => {
    const job = jobRef.current;
    jobRef.current = null;
    job?.stream.getTracks().forEach((track) => track.stop());
    setExporting(false);
  }, []);

  const finish = useCallback(() => {
    const job = jobRef.current;
    if (!job) return;
    try {
      if (job.frames.length) {
        const blob = encodeGif(job.frames, job.canvas.width, job.canvas.height);
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `simulation-${job.runId}.gif`;
        anchor.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        trackBuilderEvent("builder_results_gif_downloaded", { builder_id: simulationId, tab: "results", run_id: job.runId });
      } else {
        setError("No GIF frames were captured yet. Let the first map frame load before stopping.");
      }
    } catch (failure) {
      setError(`Could not create GIF (${String(failure?.message || failure)}).`);
    } finally {
      cancel();
    }
  }, [cancel, setError, simulationId]);

  useEffect(() => () => {
    jobRef.current?.stream.getTracks().forEach((track) => track.stop());
  }, []);

  useEffect(() => { if (!isVisible) cancel(); }, [cancel, isVisible]);
  useEffect(() => { cancel(); }, [cancel, selectedRunId]);

  const start = useCallback(async () => {
    if (!selectedRunId || exporting) return;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setError("GIF export requires a browser with tab capture support.");
      return;
    }
    setError("");
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser" }, audio: false, selfBrowserSurface: "include", preferCurrentTab: true });
      if (stream.getVideoTracks()[0]?.getSettings().displaySurface !== "browser") {
        throw new Error("Please select this browser tab in the sharing dialog.");
      }
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      const stage = stageRef.current;
      if (!stage) throw new Error("The results map is unavailable.");
      const rect = stage.getBoundingClientRect();
      const scale = Math.min(1, 720 / rect.width, 540 / rect.height);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(rect.width * scale));
      canvas.height = Math.max(1, Math.round(rect.height * scale));
      // Result artifacts are saved every 30 simulation seconds, so every
      // two-minute timestamp has a corresponding saved frame.
      const frameStep = 120;
      const times = [];
      for (let value = 0; value <= totalTime; value += frameStep) times.push(value);
      jobRef.current = { stream, video, canvas, times, index: 0, frames: [], runId: selectedRunId };
      stream.getVideoTracks()[0].addEventListener("ended", () => {
        if (jobRef.current?.stream === stream) {
          jobRef.current = null;
          setExporting(false);
          setError("GIF export stopped because tab sharing ended.");
        }
      });
      setPlaying(false);
      setProgress(0);
      setExporting(true);
      setTime(times[0]);
    } catch (failure) {
      stream?.getTracks().forEach((track) => track.stop());
      setError(`Could not start GIF export (${String(failure?.message || failure)}).`);
    }
  }, [exporting, selectedRunId, setError, setPlaying, setTime, stageRef, totalTime]);

  useEffect(() => {
    const job = jobRef.current;
    if (!exporting || !job || !isVisible || loading || loadedTime !== time || job.times[job.index] !== time) return undefined;
    const timer = window.setTimeout(() => {
      if (jobRef.current !== job) return;
      try {
        const rect = stageRef.current.getBoundingClientRect();
        const sourceScaleX = job.video.videoWidth / window.innerWidth;
        const sourceScaleY = job.video.videoHeight / window.innerHeight;
        const context = job.canvas.getContext("2d", { willReadFrequently: true });
        context.drawImage(job.video, rect.left * sourceScaleX, rect.top * sourceScaleY, rect.width * sourceScaleX, rect.height * sourceScaleY, 0, 0, job.canvas.width, job.canvas.height);
        job.frames.push(context.getImageData(0, 0, job.canvas.width, job.canvas.height));
        job.index += 1;
        setProgress(Math.round(job.index * 100 / job.times.length));
        if (job.index < job.times.length) {
          setTime(job.times[job.index]);
        } else finish();
      } catch (failure) {
        cancel();
        setError(`Could not create GIF (${String(failure?.message || failure)}).`);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [cancel, exporting, finish, isVisible, loadedTime, loading, setError, setTime, stageRef, time]);

  return { start, finish, exporting, progress };
}
